import {
  isHttpStatusError,
  isInvalidRequestError,
  isInvalidResponseError,
  isTimeoutError,
  isTransportError,
  type CommandError,
  type SDKCommand
} from '@st/sdk'
import { Effect } from 'effect'
import type { ErrorReportingService } from '~/effects/error-reporting'

type SDKFailure = CommandError
type SDKErrorSource = 'app' | 'auth'

type CommandErrorInfo = {
  request: SDKCommand
  error: SDKFailure
}

export function createSdkRequestErrorCaptureHandler({
  errorReporting,
  source
}: {
  errorReporting: ErrorReportingService
  source: SDKErrorSource
}) {
  return ({ request, error }: CommandErrorInfo) =>
    reportHandledSdkRequestError({
      errorReporting,
      source,
      request,
      error
    })
}

function reportHandledSdkRequestError({
  errorReporting,
  source,
  request,
  error
}: {
  errorReporting: ErrorReportingService
  source: SDKErrorSource
  request: SDKCommand
  error: SDKFailure
}): Effect.Effect<void> {
  if (!shouldCaptureSdkFailure(error)) {
    return Effect.void
  }

  return errorReporting.captureException(error, {
    tags: {
      handled: true,
      source,
      requestType: request.type,
      errorTag: error._tag,
      httpStatus: isHttpStatusError(error) ? error.response.status : undefined,
      transportFailureType: isTransportError(error) ? error.classification?.type : undefined,
      invalidRequestReason: isInvalidRequestError(error) ? error.reason : undefined,
      invalidResponseReason: isInvalidResponseError(error) ? error.reason : undefined
    },
    extra: {
      requestType: request.type,
      request,
      error: buildErrorPayload(error)
    }
  })
}

// Only failures that point at a bug of ours: server errors, and requests the
// server rejected as malformed. Domain outcomes (401, 403, 404, 409, 429) are
// handled by their callers, and transport failures and timeouts are mostly the
// person's network; both stay as breadcrumbs.
function shouldCaptureSdkFailure(error: SDKFailure): boolean {
  if (!isHttpStatusError(error)) return false
  const { status } = error.response
  return status >= 500 || status == 400 || status == 422
}

function buildErrorPayload(error: SDKFailure) {
  if (isHttpStatusError(error)) {
    return {
      type: 'HttpStatusError' as const,
      request: error.request,
      response: error.response
    }
  }

  if (isTransportError(error)) {
    return {
      type: 'TransportError' as const,
      request: error.request,
      classification: error.classification
    }
  }

  if (isTimeoutError(error)) {
    return {
      type: 'TimeoutError' as const,
      request: error.request,
      timeoutMs: error.timeoutMs
    }
  }

  if (isInvalidRequestError(error)) {
    return {
      type: 'InvalidRequestError' as const,
      request: error.request,
      reason: error.reason
    }
  }

  if (isInvalidResponseError(error)) {
    return {
      type: 'InvalidResponseError' as const,
      request: error.request,
      reason: error.reason,
      response: error.response
    }
  }

  return { type: 'UnknownError' as const }
}
